/** Admin plugins — read what's installed + uninstall / roll-now.
 *
 * Install + publish moved to `endpoints/catalog.ts` in Feature 21. The
 * multipart wheel-upload variant was removed in Stage 6.
 */
import { apiGet, apiPost } from '../client.js';
import type {
  InstallJobOut,
  PluginRestartOut,
  PluginServer,
  PluginStatusOut,
  RollStatus,
  UninstallResponse,
} from '../types.js';

export interface UninstallPluginArgs {
  name: string;
  /**
   * When true, the agent also removes auto-installed `optum-*` deps
   * whose only dependent was `name`. Default false — bare uninstall
   * touches only the target row.
   */
  cascade?: boolean;
}

export function uninstallPlugin(
  args: UninstallPluginArgs,
): Promise<UninstallResponse> {
  return apiPost<UninstallResponse>('/api/v1/admin/plugins/uninstall', {
    name: args.name,
    cascade: args.cascade ?? false,
  });
}

export function rollPluginsNow(): Promise<void> {
  return apiPost<void>('/api/v1/admin/plugins/roll-now', {});
}

/**
 * Whether new plugin code is waiting on a restart, and what it would cost.
 *
 * Answers for the process that serves the request. Installing a plugin does
 * not activate it — the code goes live only when the process restarts.
 */
export function getRollStatus(): Promise<RollStatus> {
  return apiGet<RollStatus>('/api/v1/admin/plugins/roll-status');
}

/**
 * Every plugin's execution server in the process that serves the request:
 * the supervisor's state and, while ready, the server's own status.
 */
export function listPluginServers(): Promise<PluginServer[]> {
  return apiGet<PluginServer[]>('/api/v1/admin/plugins/servers');
}

/**
 * Run the boot's install of an infra distribution now, even when its version
 * is current (Feature 84). The job is followed like any install job.
 */
export function reinstallPlugin(name: string): Promise<InstallJobOut> {
  return apiPost<InstallJobOut>(`/api/v1/admin/plugins/${encodeURIComponent(name)}/reinstall`, {});
}

/** Disable an infra distribution: its server stops and boots keep it disabled. */
export function disablePlugin(name: string): Promise<PluginStatusOut> {
  return apiPost<PluginStatusOut>(`/api/v1/admin/plugins/${encodeURIComponent(name)}/disable`, {});
}

/** Enable a disabled infra distribution: its server starts, or it is reinstalled. */
export function enablePlugin(name: string): Promise<PluginStatusOut> {
  return apiPost<PluginStatusOut>(`/api/v1/admin/plugins/${encodeURIComponent(name)}/enable`, {});
}

/**
 * Restart a capability plugin's server (Feature 87): it drains, its running
 * tasks finish, then a fresh server starts. Answers at once; follow the
 * server's state on the servers list.
 */
export function restartPluginServer(name: string): Promise<PluginRestartOut> {
  return apiPost<PluginRestartOut>(`/api/v1/admin/plugins/${encodeURIComponent(name)}/restart`, {});
}
